import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  APP_ZONES,
  BaseCssProperties,
  BaseWidgetProperties,
  Constants,
  DatePickerTypes,
  LayoutWidgetConfig,
  ProfileStateModel,
  ProviderClass,
  UiZoneWidgetConfig,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../../lib/services/multipart-holder/multipart-form-holder.service';

import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import {
  checkMinAgeValidation,
  validDayOfMonthYearValidator,
} from '@app/finxone-web-frontend/app/lib/utils/validators/validators.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { Store } from '@ngxs/store';
import { FormActionTypeEnum } from '../../../../../../../../../libs/shared/enums/form-action.enum';
import { Subject, debounceTime, map } from 'rxjs';
import { StyleServiceService } from '../../../../../lib/services/style-service/style-service.service';
import { formatNameAsUrl } from '../../../../../lib/utils/zone-url.utils';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-birthday',
  templateUrl: './birthday.component.html',
  styleUrls: ['./birthday.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BirthdayComponent extends FormSubmitter implements OnInit {
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  public zone = formatNameAsUrl(APP_ZONES.ONBOARDING);
  public widgetConfig: UiZoneWidgetConfig;
  public totalSteps: number;
  public birthdayForm: FormGroup;
  public inputDatePickerForm: FormGroup;
  public isStepComplete = false;
  public isError = false;
  public showPopup = false;
  public widgetsAttributes: LayoutWidgetConfig[] = [];
  public birthdayString = '';
  public urlParams: string[] = [];
  public isShowErrorMsg = false;
  public isAnyFieldRequired = false;
  public maxDate: Date = new Date();
  public minAge = 0;
  public currentWidgetPosition: number;
  public colorTextAlignStyle: baseCssConfigStyle;
  public colorStyle: baseCssConfigStyle;
  public borderStyle: baseCssConfigStyle;
  public isWorkflow: boolean;
  public nextPageUrl: string;
  public birthdayFormValue: { [key: string]: Date } = {};
  public datepickerTypes = DatePickerTypes;
  public allmonths = Constants.allMonths;
  public selectedMonth = 0;
  public selectedDay = 1;
  public selectedYear: number;
  public maxYear = new Date().getFullYear();
  public minYear = 1900;

  public isBirthdayShowSuccessIcon = false;
  public isBirthdayShowErrorIcon = false;
  public textInputFontStyle: baseCssConfigStyle;
  public textInputBackgroundColor: baseCssConfigStyle;
  combinedContentAndBorderStyles: baseCssConfigStyle;
  private isValueChanged = false;
  public inputFieldDayStyle: baseCssConfigStyle;
  public inputFieldMonthStyle: baseCssConfigStyle;
  public inputFieldYearStyle: baseCssConfigStyle;
  private inputStyle: baseCssConfigStyle;
  private inputBaseStyle: baseCssConfigStyle;
  private inputInactiveStyle: baseCssConfigStyle;
  private userProfileData: ProfileStateModel;

  constructor(
    private configService: ConfigService,
    private formHolderService: MultiPartFormHolderService,
    public dataIn: ProviderClass<any>,
    private urlZoneInput: ZonePageUrlProvider,
    protected override store: Store,
    private formSubmissionService: FormSubmissionService,
    private styleService: StyleServiceService,
    private datePipe: DatePipe,
    public pageConfig: PageConfigurationProvider,
  ) {
    super(store);
    this.zone = this.urlZoneInput.zoneUrlInput;
  }

  ngOnInit(): void {
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;

    if (this.dataIn != undefined && this.dataIn != null) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }
    this.userProfileData = this.store.selectSnapshot(ProfileState.getProfile);
    const minAge_config = this.widgetConfig.attributes['field_attributes'][0].decision_config.minimum_age;

    if (minAge_config) {
      this.minAge = minAge_config;
    }

    const dobValue =
      this.zone !== formatNameAsUrl(APP_ZONES.ONBOARDING) && this.userProfileData?.dob
        ? this.userProfileData.dob
        : '';
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.BIRTHDAY_WIDGET,
      route: '/onboard-individual/register',
      form: new FormGroup({
        date_of_birth: new FormControl(dobValue !== '' ? new Date(dobValue) : null, [
          Validators.required,
          this.minimumAge(this.minAge),
        ]),
      }),
    });

    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);
    this.buildForm();
    const styles = this.styleService.generateStyles(this.attri);
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);
    this.colorTextAlignStyle = {
      ...styles.colorTextAlignFontStyle,
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
      ]),
    };
    this.colorStyle = styles.colorStyle;
    this.borderStyle = {
      ...styles.borderStyle,
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.BORDER,
        BaseCssProperties.BACKGROUND_COLOR,
      ]),
    };
    this.textInputBackgroundColor = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.TEXT_INPUT_BG_COLOR,
      BaseCssProperties.INPUT_IDLE_BORDER_COLOR,
    ]);
    this.textInputFontStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.TEXT_INPUT_FONT_SIZE,
    ]);
    this.combinedContentAndBorderStyles = {
      ...this.contentStyle,
      ...this.borderStyle,
    };

    this.subscribeToFormAction().subscribe((response) => {
      if (response?.nextPageUrl) this.nextPageUrl = response.nextPageUrl;
      if (response?.type === 'submitStep') this.onSubmit();
      if (response?.formData?.['date_of_birth']) this.patchFormValues(response.formData);
    });

    this.getAndAssignInputFieldsStyles(this.attri.widgetProperties);
    if (this.widgetConfig.attributes?.widgetProperties?.dateType === DatePickerTypes.INPUT) {
      this.selectedYear = this.selectedYear ? this.selectedYear : new Date().getFullYear() - this.minAge;
      this.createInputDatePickerForm();
    }
    this.checkBirtdaySuccessErrorIconFlag();
  }

  private getAndAssignInputFieldsStyles(widgetProperties: BaseWidgetProperties) {
    const { baseStyle, activeStyle, inactiveStyle } =
      this.configService.getInputFieldCssStyles(widgetProperties);

    this.inputStyle = baseStyle;
    this.inputBaseStyle = activeStyle;
    this.inputInactiveStyle = inactiveStyle;
    this.inputFieldDayStyle =
      this.inputFieldMonthStyle =
      this.inputFieldYearStyle =
        {
          ...this.inputInactiveStyle,
          ...this.inputBaseStyle,
        };
  }

  private checkBirtdaySuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isBirthdayShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isBirthdayShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }
  public valueChangesTrigger() {
    this.isValueChanged = true;
  }
  createInputDatePickerForm() {
    this.inputDatePickerForm = this.createInputAgeDatePickerForm(
      this.selectedDay,
      this.selectedMonth,
      this.selectedYear,
      this.minYear,
      this.maxYear,
      this.minAge,
    );
    this.inputDatePickerForm.valueChanges.subscribe(() => {
      const dateFormValues = this.inputDatePickerForm.value;
      if (this.inputDatePickerForm.invalid) {
        this.birthdayForm?.get('date_of_birth')?.setValue(null);
        this.syncValue();
      } else {
        const myDate = new Date();
        myDate.setDate(1);
        myDate.setHours(0, 0, 0, 0);
        myDate.setFullYear(dateFormValues.year);
        myDate.setMonth(dateFormValues.month);
        myDate.setDate(dateFormValues.day);
        this.birthdayForm?.get('date_of_birth')?.setValue(myDate);
      }
    });
  }
  get day() {
    return this.inputDatePickerForm?.get('day');
  }
  get year() {
    return this.inputDatePickerForm?.get('year');
  }

  buildForm() {
    const stage = this.formHolderService.config.getStage(WidgetNames.BIRTHDAY_WIDGET);

    this.birthdayForm = stage.getForm();
    if (this.widgetsAttributes.length) {
      const formAttributes = this.widgetsAttributes[0].attributes;
      formAttributes.field_attributes.forEach((field: any) => {
        if (field.decision_config.required === true) {
          if (field.decision_config.minimum_age) {
            this.minAge = field.decision_config.minimum_age;
            this.maxDate.setFullYear(new Date().getFullYear() - this.minAge);
          }
          this.isAnyFieldRequired = true;
        }
      });
    }
    if (this.widgetConfig.attributes?.widgetProperties?.dateType === this.datepickerTypes.INPUT) {
      this.birthdayFormValue = this.birthdayForm.value;
      this.setInputDatePickerValue();
    }
    if (this.zone === formatNameAsUrl(APP_ZONES.VERIFICATION)) {
      this.birthdayForm.valueChanges
        .pipe(
          debounceTime(500),
          map(() => {
            if (this.birthdayForm.valid) {
              this.formSubmissionService.storeFormData(
                this.birthdayForm.value,
                FormActionTypeEnum.KYC_DETAILS,
              );
            }
          }),
        )
        .subscribe();
    }
  }

  patchFormValues(formData: Record<string, string>) {
    this.birthdayFormValue['date_of_birth'] = new Date(formData?.['date_of_birth']);
    let birthday = '';
    Object.values(this.birthdayFormValue).forEach((value: string | Date) => {
      birthday = birthday + value + ' ';
    });
    this.birthdayString = birthday;
    if (this.widgetConfig.attributes?.widgetProperties?.dateType === this.datepickerTypes.INPUT) {
      this.setInputDatePickerValue();
    }

    this.isStepComplete = true;
    this.isError = false;
  }

  setInputDatePickerValue() {
    this.selectedYear = new Date(this.birthdayFormValue['date_of_birth']).getFullYear();
    this.selectedDay = new Date(this.birthdayFormValue['date_of_birth']).getDate();
    this.selectedMonth = new Date(this.birthdayFormValue['date_of_birth']).getMonth();
  }

  onSubmit() {
    if (this.birthdayForm.valid) {
      this.syncValue();
    } else {
      this.birthdayForm.markAllAsTouched();
      this.isStepComplete = false;
      this.isError = true;
      this.showPopup = true;
    }
  }

  showErrorMsg() {
    this.isShowErrorMsg = !this.isShowErrorMsg;
  }

  private minimumAge(age: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null) {
        return null;
      }
      function subtractYears(date: Date, years: number) {
        date.setFullYear(date.getFullYear() - years);
        return date;
      }
      const minimumDate = subtractYears(new Date(), age);
      if (!(control.value instanceof Date)) return null;
      return control.value < minimumDate
        ? null
        : {
            'date-minimum': {
              'age-minimum': age,

              'actual-dob': control.value.toDateString(),
            },
          };
    };
  }

  syncValue() {
    const formValues: Record<string, string> = this.birthdayForm.value;

    this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then((success) => {
      if (success) {
        let birthday = '';
        Object.values(formValues).forEach((value: string) => {
          birthday = birthday + value + ' ';
        });
        this.birthdayString = birthday;
        this.isStepComplete = true;
        this.isError = false;
        // Form submission was successful, you can handle it here
      } else {
        // Handle submission failure
      }
    });
    this.isStepComplete = true;
    this.isError = false;
  }

  public resetYear(): void {
    if (this.inputDatePickerForm.get('year')?.untouched) this.inputDatePickerForm.get('year')?.setValue('');
  }

  public resetDay(): void {
    if (this.inputDatePickerForm.get('day')?.untouched) this.inputDatePickerForm.get('day')?.setValue('');
  }

  public updateInputFieldCurrentStyles(event: boolean, fieldName: string) {
    const inActiveStyles = {
      ...this.inputInactiveStyle,
      ...this.inputBaseStyle,
    };
    const activeStyles = {
      ...this.inputStyle,
      ...this.inputBaseStyle,
    };
    switch (fieldName) {
      case 'day':
        this.inputFieldDayStyle = event ? activeStyles : inActiveStyles;
        break;
      case 'month':
        this.inputFieldMonthStyle = event ? activeStyles : inActiveStyles;
        break;
      case 'year':
        this.inputFieldYearStyle = event ? activeStyles : inActiveStyles;
        break;
      default:
        break;
    }
  }

  private createInputAgeDatePickerForm(
    selectedDay: number,
    selectedMonth: number,
    selectedYear: number,
    minYear: number,
    maxYear: number,
    minAge: number,
  ): FormGroup {
    return new FormGroup(
      {
        day: new FormControl(selectedDay, [Validators.required, Validators.min(1)]),
        month: new FormControl(selectedMonth, [Validators.required]),
        year: new FormControl(selectedYear, [
          Validators.required,
          Validators.min(minYear),
          Validators.max(maxYear),
        ]),
      },
      [validDayOfMonthYearValidator('year', 'month', 'day'), checkMinAgeValidation(minAge)],
    );
  }
}
